/**
 * Korean message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: 해요체 (polite informal, locked in
 * l10n/locales.md) — the friendly modern-app voice (~요/~하세요).
 *
 * Platform terms follow Korean UI conventions (설정, 페어링 for
 * pairing, 미러링 for mirroring, 삭제 for dismiss, 대화 for
 * conversation). "Glance" is the untranslated feature brand name
 * (glossary D1); "클립" renders the D2 loanword in Hangul.
 *
 * Plural notes: Korean is an `other`-only CLDR locale — counter words
 * (개, 대, 건, 자) carry the number; every `selectPlural("ko", …)`
 * supplies only an `other` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "연결됨",
    statusReconnecting: "다시 연결하는 중…",
    statusDisconnected: "연결 끊김",
    openSettings: "설정 열기",
    pairedFallbackDevice: "내 휴대전화",
    publicModeToggleEnable: "알림 내용 숨기기",
    publicModeToggleDisable: "알림 내용 표시",
  },

  notifications: {
    replyAriaLabel: "답장",
    replyPlaceholder: "답장…",
    replySend: "보내기",
    sent: "보냈어요",
    failedToSend: "보내지 못했어요",
    unread: "읽지 않음",
    dismiss: "삭제",
    notificationImageAlt: "알림 이미지",
  },

  conversations: {
    replyAriaLabel: "답장",
    replyPlaceholder: "답장…",
    replySend: "보내기",
    sent: "보냈어요",
    failedToSend: "보내지 못했어요",
    dismiss: "대화 삭제",
    messagesListLabel: "이 대화의 메시지",
    deviceOwner: "나",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "읽지 않은 메시지 없음"
        : `읽지 않은 메시지 ${count}${selectPlural("ko", count, { other: "개" })}`,
  },

  list: {
    today: "오늘",
    yesterday: "어제",
    searchNotifications: "알림 검색",
    clearSearch: "검색어 지우기",
    clearAll: "모두 삭제",
    markAllRead: "모두 읽음으로 표시",
    groupCheckboxLabel: "그룹으로 묶기",
    noNotificationsTitle: "아직 알림이 없어요",
    noNotificationsDesc: "휴대전화에서 미러링된 새 메시지가 여기에 표시돼요.",
    noMatchesTitle: "일치하는 항목 없음",
    notificationsList: "알림",
    privacyAppList: "앱별 알림",
    clear: "삭제",
    clearMatches: (count: number): string =>
      `일치 항목 ${count}${selectPlural("ko", count, { other: "개" })} 삭제`,
    noMatchesDesc: (query: string): string => `"${query}"과(와) 일치하는 알림이 없어요.`,
    appNotificationsGroup: (appName: string): string => `${appName} 알림`,
    dismissAllFrom: (appName: string): string => `${appName}의 알림 모두 삭제`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const unit =
        kind === "messages"
          ? selectPlural("ko", count, { other: "개" })
          : kind === "calls"
            ? selectPlural("ko", count, { other: "건" })
            : selectPlural("ko", count, { other: "개" });
      const noun = kind === "messages" ? "메시지" : kind === "calls" ? "전화" : "알림";
      return `${noun} ${count}${unit}`;
    },
    kbdDismiss: "삭제",
    kbdNavigate: "이동",
    kbdOrSeparator: "또는",
  },

  settings: {
    back: "알림으로 돌아가기",
    settings: "설정",
    general: "일반",
    theme: "테마",
    themeAuto: "자동",
    themeLight: "라이트",
    themeDark: "다크",
    language: "언어",
    languageSystemDefault: "시스템 기본값",
    notificationsSection: "알림",
    osNotifications: "OS 알림",
    callNotifications: "전화 알림",
    privacySecurity: "개인정보",
    analyticsToggle: "익명 사용 통계",
    analyticsToggleDescription: "횟수와 마일스톤만 — 알림 내용은 절대 포함하지 않아요.",
    statusConnected: "연결됨",
    statusReconnecting: "다시 연결하는 중…",
    statusDisconnected: "연결 끊김",
    disconnectFromPhone: "휴대전화 연결 끊기",
    disconnectConfirm: "정말 끊을까요? 기기의 페어링이 해제돼요.",
    yesDisconnect: "네, 연결을 끊을게요",
    cancel: "취소",
    version: "버전",
    encryption: "암호화",
    encryptionValue: "E2E (NaCl)",
    needHelp: "도움이 필요하세요? __EMAIL__(으)로 메일을 보내세요",
    disconnectFailed: "연결을 끊지 못했어요 — 다시 시도해 주세요",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `연결은 끊었지만 로컬 정리에 실패했어요: ${errorMsg}. 페이지를 새로고침해 주세요.`,
    disconnectLocalCleanupFailed:
      "연결은 끊었지만 로컬 정리에 실패했어요. 페이지를 새로고침해 주세요.",
  },

  pairing: {
    enterCodeInstruction: "이 코드를 Reflecto 앱에 입력하세요",
    pairedSuccessfully: "페어링이 완료됐어요!",
    pairingFailed: "페어링에 실패했어요",
    requestingPairingCode: "페어링 코드를 요청하는 중…",
    codeExpired: "코드가 만료됐어요",
    tryAgain: "다시 시도",
    failedToStartPairing: "페어링을 시작하지 못했어요",
    installHeading: "아직 앱이 없나요?",
    installScanHint: "휴대전화 카메라로 스캔하세요",
    installQrLabel: "휴대전화에 Reflecto를 설치하는 QR 코드",
    capabilitiesHeading: "페어링하면 이런 걸 할 수 있어요:",
    capabilityMirror: "휴대전화 알림을 여기서 확인",
    capabilityReply: "휴대전화를 들지 않고 답장",
    capabilityShare: "클립보드와 파일을 양방향으로 공유",
    encryptionNote: "종단 간 암호화돼요. 서버는 내용을 절대 보지 않아요.",
  },

  dnd: {
    title: "방해 금지",
    description: "이 기기의 알림을 1시간 동안 일시중지해요.",
    snooze1h: "1시간 일시중지",
    snoozeActiveUntil: (formattedTime: string): string => `${formattedTime}까지 적용`,
    cancelSnooze: "취소",
  },

  connectors: {
    title: "연결된 서비스",
    emptyBody:
      "아직 연결된 서비스가 없어요. ChatGPT나 Claude의 커넥터 설정에서 연결한 다음, 휴대전화에서 코드를 승인하세요.",
    scopeSend: "알림을 보낼 수 있어요",
    added: (date: string): string => `추가됨 ${date}`,
    lastUsed: (relative: string): string => `마지막 사용 ${relative}`,
    neverUsed: "사용한 적 없음",
    revoke: "연결 해제",
    revokeConfirm: (label: string): string =>
      `${label}의 연결을 해제할까요? 즉시 접근할 수 없게 돼요.`,
    revokeAction: "연결 해제",
    revokeConfirmLabel: (label: string): string => `${label} 연결 해제`,
    cancel: "취소",
    loadError: "연결된 서비스를 불러오지 못했어요. 다시 시도해 주세요.",
    revokeError: "서비스 연결을 해제하지 못했어요. 다시 시도해 주세요.",
    retry: "다시 시도",
  },

  safetyNumber: {
    rowLabel: "암호화 확인",
    rowSublabel: "휴대전화의 보안 번호와 비교",
    cardTitle: "보안 번호",
    caption: (deviceLabel: string): string =>
      `${deviceLabel}에 표시된 번호와 비교하세요. 둘 다 일치하면 종단 간 암호화가 유지돼요.`,
    repairNote: "이 번호는 기기를 다시 페어링할 때 변해요.",
    copy: "복사",
    copied: "복사됨",
    maskedHint: "개인정보 보호 모드가 켜져 있는 동안에는 숨겨져요",
  },

  feedback: {
    title: "의견 보내기",
    close: "닫기",
    messageLabel: "메시지",
    messagePlaceholder: "버그를 찾았거나, 아이디어가 있거나, 뭔가 이상한가요? 들려주세요.",
    categoryLabel: "분류",
    categoryBug: "버그",
    categoryIdea: "아이디어",
    categoryOther: "기타",
    emailLabel: "이메일 (선택 사항, 답변이 필요할 때만)",
    emailPlaceholder: "you@example.com",
    emailHelp: "어떤 목록에도 추가하지 않아요.",
    disclosure:
      "보내주신 메시지는 Reflecto 서버로 전송되어 내부 이슈 트래커에 기록돼요. 리포트를 연결하기 위해 앱 버전과 페어링할 때 부여된 기기 ID를 함께 첨부해요. 알림, 연락처, 그 밖의 개인 데이터는 볼 수 없어요. 이메일은 선택 사항이며 답변이 필요할 때만 적어 주세요. 어떤 목록에도 추가하지 않아요.",
    submit: "의견 보내기",
    sending: "보내는 중…",
    success: "감사해요 — 보통 24시간 안에 사람이 직접 읽어요",
    error: "보내지 못했어요. 다시 시도해 주세요.",
    done: "완료",
    sendFeedback: "의견 보내기",
  },

  uninstall: {
    headline: "Reflecto 확장 프로그램을 제거했어요",
    body: "계정은 처음부터 없었고, 확장 프로그램은 페어링 정보를 이 컴퓨터에만 보관했어요. 따로 정리할 게 남아 있지 않아요.",
    ask: "30초만 시간이 있다면, 무엇이 잘못됐는지 알려 주세요.",
    categoryLegend: "어떤 종류의 의견인가요?",
    categoryBug: "뭔가 고장 났어요",
    categoryIdea: "필요한 기능이 없었어요",
    categoryOther: "그 밖의 이야기",
    messageLabel: "무엇이 잘못됐나요?",
    messagePlaceholder: "몇 시간 지나니 알림이 오지 않았어요.",
    messageRequired: "먼저 한 문장을 적고 보내 주세요.",
    charactersLeft: (count: number): string => `${count}자 남음`,
    emailLabel: "이메일 (선택 사항)",
    emailPlaceholder: "you@example.com",
    emailHelp: "답장할 때만 사용해요. 그 외에는 쓰지 않아요.",
    submit: "의견 보내기",
    sending: "보내는 중…",
    retry: "다시 시도",
    privacyNote:
      "이 양식은 어떤 기기와도 연결되어 있지 않아요. 알림, 클립보드, 파일 내용은 전혀 담기지 않아요. 이메일은 선택 사항이고 답장에만 사용해요.",
    success: "감사해요. 보내 주신 내용은 하나하나 사람이 읽어요.",
    errorGeneric: "보내지 못했어요. 작성한 내용은 그대로 있어요.",
    errorRateLimited:
      "최근에 메시지를 여러 번 보내셨어요. 한 시간쯤 뒤에 다시 시도해 주세요. 작성한 내용은 그대로 남아 있어요.",
    footerWebApp: "Reflecto 웹 앱은 확장 프로그램 없이도 쓸 수 있어요.",
    footerWebAppLink: "웹 앱 열기",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "닫기",
    aiSummaryTag: "AI 요약",
    errorAi: "지금은 요약을 만들지 못했어요.",
    retryCta: "다시 시도",
    downloadHeading: "AI 요약 받아보기",
    downloadBody:
      "기기에서 바로 동작하는 AI 요약으로 놓친 알림을 따라잡으세요. 전부 내 기기 안에서만 실행돼요.",
    downloadCta: "모델 다운로드",
    downloadPrivacyNote: "다운로드는 한 번뿐이에요. 서버는 알림을 절대 볼 수 없어요.",
    downloadingHeading: "모델 다운로드 중…",
    caughtUp: "모두 확인했어요 — 지금은 챙길 일이 없어요.",
    needsYou: "확인 필요",
    quiet: "그 외",
    scanning: (pending: number): string => `${pending}개 요약 중…`,
    jump: "알림 열기",
    affordanceReply: "답장",
    affordanceOpen: "열기",
    affordanceCopyOtp: "코드 복사",
    buttonLabel: "Glance",
  },

  update: {
    title: "업데이트가 있어요",
    description: "새 버전의 Reflecto가 준비됐어요. 새로고침해서 업데이트하세요.",
    reload: "새로고침",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `이 기기의 이름을 "${name}"(으)로 정했어요.`,
    cardLooksGood: "좋아요",
    cardEdit: "수정",
    cardAriaLabel: "기기 이름 확인",
    renameTitle: "이 기기 이름 변경",
    renameLabel: "기기 이름",
    renameSave: "저장",
    renameCancel: "취소",
    renameFailed: "이름을 변경하지 못했어요. 다시 시도해 주세요.",
  },

  collision: {
    title: (name: string): string => `이미 "${name}"이(가) 있어요`,
    subtitle: "이 기기를 뭐라고 부를까요?",
    chipWork: (name: string): string => `직장 ${name}`,
    chipHome: (name: string): string => `집 ${name}`,
    chipPersonal: (name: string): string => `개인 ${name}`,
    customLabel: "직접 입력",
    save: "저장",
    useAnyway: "원래 이름 그대로 사용",
    saveFailed: "새 이름을 저장하지 못했어요. 다시 시도해 주세요.",
    ariaLabel: "중복된 기기 이름 해결",
  },

  clipboard: {
    title: "클립보드",
    emptyTitle: "아직 클립이 없어요",
    empty: "다른 기기의 클립이 여기에 표시돼요. 탭해서 복사하세요.",
    from: (label: string): string => `${label}에서`,
    to: (label: string): string => `${label}(으)로`,
    copy: "클립보드에 복사",
    copied: "복사했어요",
    reveal: "표시",
    hide: "숨기기",
    sensitiveChip: "민감한 내용",
    cloudChip: "내 API 토큰 경유",
    maskedHint: '내용이 숨겨져 있어요 — "표시"를 눌러 확인하세요',
    delete: "클립 삭제",
    sendPlaceholder: "보낼 텍스트 붙여넣기…",
    sendCta: "보내기",
    sendSensitiveToggle: "민감한 내용으로 표시",
    sendSuccess: (n: number): string =>
      `기기 ${n}${selectPlural("ko", n, { other: "대" })}로 보냈어요.`,
    sendPartial: (n: number, total: number): string =>
      `기기 ${total}${selectPlural("ko", total, { other: "대" })} 중 ${n}대로 보냈어요.`,
    sendNoDevices: "보낼 수 있는 페어링된 기기가 없어요.",
    sendFailed: "보내지 못했어요. 다시 시도해 주세요.",
    sendEmpty: "보낼 내용이 없어요.",
    dockRecentHeading: "최근",
    dockEmptyClipboard: "클립보드가 비어 있어요.",
    clearAll: "모두 삭제",
    clearAllConfirm: "네, 모두 삭제할게요",
    clearAllCancel: "취소",
    shareTitle: "내 기기로 보내기",
    shareReview: "보낼 내용을 확인하세요. 페어링된 기기로 종단 간 암호화되어 전송돼요.",
    shareSend: "보내기",
    shareCancel: "취소",
    shareNotPaired: "공유한 텍스트를 보내려면 먼저 기기를 페어링하세요.",
    shareOpenApp: "Reflecto 열기",
    pasteFromClipboard: "클립보드에서 붙여넣기",
    readFailed: "클립보드를 읽을 수 없습니다. 아래에 붙여넣으세요.",
  },
  install: {
    title: "Reflecto 설치",
    body: "홈 화면에 추가하면 더 빠르게 열 수 있어요.",
    cta: "설치",
    dismissAria: "설치 안내 닫기",
  },
  nav: {
    home: "홈",
    clipboard: "클립보드",
    files: "파일",
    ariaLabel: "기본 탐색",
    fileHiddenPublicMode: "개인 정보 모드가 켜져 있는 동안 숨겨집니다.",
  },
  common: {
    loading: "로드 중",
  },
  errors: {
    notPaired: "페어링되지 않음",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os}의 ${browser}`,
    webFallback: "웹 브라우저",
  },
  palette: {
    placeholder: "명령어 입력…",
    noResults: "일치하는 명령 없음",
    ariaLabel: "명령어 팔레트",
    clearAll: "모든 알림 삭제",
    glance: "Glance 요약",
    cancelSnooze: "일시중지 취소",
    copyClip: "마지막으로 받은 클립 복사",
  },

  fileTransfer: {
    sendTitle: "파일 보내기",
    sendChooseFile: "파일 선택",
    sendDeviceLabel: "보낼 대상",
    sendPhoneFallbackName: "휴대전화",
    send: "파일 보내기",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "준비 중…" : `준비 중 — ${pct}%`,
    sendDelivering: "휴대전화로 전달 중…",
    sendWaitingAccept: "휴대전화에서 수락하기를 기다리는 중…",
    sendWaitingAppOpen: "수락하려면 휴대전화에서 Reflecto를 여세요",
    sendWaitingAppOpenHint:
      "휴대전화가 푸시 알림을 받을 수 없어 Reflecto를 열어야 이 파일이 표시됩니다.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `${partCount}개 중 ${partNumber}번째 파트 업로드 중…`,
    sendDone: "보냈어요.",
    sendFailedRejected: "휴대전화에서 파일을 거절했어요.",
    sendFailedNoAnswer: "휴대전화에서 응답이 없어요.",
    sendFailedUploadFailed: "업로드하지 못했어요. 다시 시도해 주세요.",
    sendFailedCancelled: "취소했어요.",
    sendFailedGeneric: "문제가 발생했어요. 다시 시도해 주세요.",
    sendTooLarge: (maxMiB: number): string => `파일이 너무 커요(최대 ${maxMiB} MiB).`,
    sendKeepOpenHint: "전송이 끝날 때까지 이 탭을 열어 두세요.",
    sendAnother: "다른 파일 보내기",
    sendNoDevice: "파일을 보내려면 먼저 휴대전화를 페어링하세요.",
    sendCancel: "취소",
    sendPeerProgress: (chunksDone: number): string => `휴대전화가 청크 ${chunksDone}개를 받았어요.`,

    receiveTitle: "수신 파일",
    receiveOfferFrom: (sender: string): string => `${sender}에서 파일을 보내려고 해요`,
    receiveAccept: "수락",
    receiveDecline: "거절",
    receiveReceiving: (n: number, total: number): string => `${total}개 중 ${n}번째 파트 수신 중…`,
    receiveDone: "저장했어요.",
    receiveFailed: "전송에 실패했어요.",
    receiveCancelled: "취소했어요.",
    receiveCancel: "취소",

    errorChunkAuthFail: "전송에 실패했어요 — 데이터를 확인할 수 없었어요. 다시 시도해 주세요.",
    errorHashMismatch:
      "전송에 실패했어요 — 파일이 무결성 검사를 통과하지 못했어요. 다시 시도해 주세요.",
    errorStorageFull: "이 전송에 필요한 저장 공간이 부족해요.",
    errorExpired: "전송이 만료됐어요.",
    errorQuotaExceeded: "일일 전송 한도에 도달했어요 — 내일 다시 시도해 주세요.",
    errorInternal: "전송에 실패했어요 — 문제가 발생했어요.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "닫기",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "전송 진행률",
    outgoing: (n: number): string => `보내는 중 (${n})`,
    incoming: (n: number): string => `받는 중 (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "모두 수락",
    dropTitle: "여기에 끌어다 놓기",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `최대 ${size} 파일.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "여기에 끌어다 놓기, 또는 보낼 파일을 선택하세요",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "직접",
    transportDirectHint: "기기 간 직접 전송 — 서버에 저장되지 않습니다.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "서버 경유",
    transportRelayHint: "서버를 거치지만 종단 간 암호화가 유지됩니다 — 내용을 읽을 수 없습니다.",
    directBadge: "직접",
  },
  time: {
    justNow: "방금 전",
    minutesAgo: (count: number): string => `${count}분 전`,
    hoursAgo: (count: number): string => `${count}시간 전`,
  },
} satisfies MessagesShape;
